import React, {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useDatelessSeoSRP} from 'hooks/useDatelessSeoSRP'
import {useDestinationDisplayName} from 'hooks/useDestinationDisplayName'
import {
  getSearchBoxValues,
  getShowRoomsLabel
} from 'modules/searchBox/selectors'
import {openSearchBox, setIsSearchBoxActive} from 'modules/searchBox/slice'
import {SearchBoxAncestor} from 'modules/searchBox/types'

import {cssTheme} from '@vio/atlas/themes'
import {logTimeStart} from '@vio/core/src/_web/utils/logging/performanceTimer'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {roomsToConfigurationObject} from '@vio/core/src/room/business/roomConfiguration'
import {dateStringToMiddayDate} from '@vio/core/src/utils/date'
import {SearchSummaryButton} from '@vio/shared/src/search/SearchSummaryButton'

interface PropsType {
  ancestor: SearchBoxAncestor
  isCompact?: boolean
  withSidePadding?: boolean
}

const Wrapper = styled.div`
  width: 100%;
  &.--padding {
    padding-block: 0;
    padding-inline: ${cssTheme.layout.spacing.s300}
      ${cssTheme.layout.spacing.s250};
  }
`

export const SearchSummaryButtonWithStore = ({
  ancestor,
  isCompact = false,
  withSidePadding = true
}: PropsType) => {
  const dispatch = useDispatch()
  const isDatelessSeoSRP = useDatelessSeoSRP()
  const {rooms, checkIn, checkOut} = useSelector(getSearchBoxValues) || {}
  const destination = useDestinationDisplayName()
  const showRoomsLabel = useSelector(getShowRoomsLabel)
  const {numberOfAdults, numberOfChildren, numberOfRooms} =
    roomsToConfigurationObject(rooms)
  const checkOutDate = dateStringToMiddayDate(checkOut)
  const checkInDate = dateStringToMiddayDate(checkIn)

  const handleClick = useCallback(() => {
    logTimeStart('SearchBoxReady')
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.SearchSummary,
        action: Action.Clicked
      })
    )
    dispatch(openSearchBox(ancestor))
    dispatch(setIsSearchBoxActive(true))
  }, [dispatch, ancestor])

  return (
    <Wrapper className={withSidePadding && '--padding'}>
      <SearchSummaryButton
        checkIn={checkInDate}
        checkOut={checkOutDate}
        destination={destination}
        numberOfAdults={numberOfAdults}
        numberOfChildren={numberOfChildren}
        numberOfRooms={numberOfRooms}
        onClick={handleClick}
        isCompact={isCompact}
        isRoomsLabelShown={showRoomsLabel}
        isRounded
        isDatelessSeoSRP={isDatelessSeoSRP}
      />
    </Wrapper>
  )
}
